import { TaskLabel } from '@seen/core/models/taskLabel/taskLabel'
import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { parseReferenceString } from '@seen/core/utils'
import { cn } from '@seen/ui'
import { compact } from 'lodash-es'
import { type HTMLAttributes, type ReactNode, useMemo } from 'react'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { LabelBadge } from '#src/components/LabelBadge.tsx'
import { LabelCircles } from '#src/components/LabelCircles.tsx'
import { TaskLabelCombobox } from '#src/components/TaskLabelCombobox.tsx'
import { RowBadge } from '#src/components/TaskRowBadge.tsx'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface LabelRowProps extends HTMLAttributes<HTMLDivElement> {
  task: StandardTask
  maxLabelsToShow?: number
}

export function LabelRow({
  task,
  className,
  maxLabelsToShow = MAX_LABELS_TO_SHOW,
  ...delegated
}: LabelRowProps): ReactNode {
  const { data: labels } = useSearch(TaskLabel, TaskLabel.baseSearchQuery())
  const labelSet = useMemo(
    () => new Set(labels?.map((l) => l.reference.reference)),
    [labels]
  )

  const validTaskLabels = useMemo(
    () => task.labels.filter((l) => !!l.reference && labelSet.has(l.reference)),
    [task.labels, labelSet]
  )

  if (validTaskLabels.length === 0) {
    return null
  }

  return (
    <div className={cn('flex items-center gap-0.75', className)} {...delegated}>
      {validTaskLabels.slice(0, maxLabelsToShow).map((label) => (
        <ResourceFieldForm
          key={label.reference}
          instance={task}
          valueFn={(t) => compact(t.labels.map((l) => l.reference))}
          patchFn={(t, value) => {
            t.labels = compact(value.map((l) => parseReferenceString(l)))
          }}
          className="h-full"
          render={({ field: { value, onChange, ...rest } }) => (
            <TaskLabelCombobox value={value} onChange={onChange}>
              <LabelBadge label={label.reference} {...rest} />
            </TaskLabelCombobox>
          )}
        />
      ))}
      {validTaskLabels.length > maxLabelsToShow && (
        <ResourceFieldForm
          instance={task}
          valueFn={(t) => compact(t.labels.map((l) => l.reference))}
          patchFn={(t, value) => {
            t.labels = compact(value.map((l) => parseReferenceString(l)))
          }}
          className="h-full"
          render={({ field: { value, onChange, ...rest } }) => (
            <TaskLabelCombobox value={value} onChange={onChange}>
              <RowBadge
                icon={
                  <LabelCircles
                    labels={compact(
                      validTaskLabels
                        .slice(maxLabelsToShow)
                        .map((l) => l.reference)
                    )}
                  />
                }
                {...rest}
              >
                {validTaskLabels.length === maxLabelsToShow + 1 && (
                  <>{maxLabelsToShow > 0 && '+'}1 label</>
                )}
                {validTaskLabels.length > maxLabelsToShow + 1 && (
                  <>
                    {maxLabelsToShow > 0 && '+'}
                    {validTaskLabels.length - maxLabelsToShow} labels
                  </>
                )}
              </RowBadge>
            </TaskLabelCombobox>
          )}
        />
      )}
    </div>
  )
}

const MAX_LABELS_TO_SHOW = 3
